import { Button, Controller } from '../../interfaces/controller';

/**
 * Standard NES controller (NES-004): an 8-bit parallel-in/serial-out shift
 * register exposed through $4016/$4017.
 *
 * Hardware behavior implemented:
 *  - While the strobe bit ($4016 bit 0) is 1, the shift register is
 *    continuously reloaded from the current button state, so reads always
 *    return button A's state.
 *  - On the 1->0 strobe transition, the current button state is latched once.
 *  - Each read returns the LSB of the shift register (A first, then B,
 *    Select, Start, Up, Down, Left, Right) and shifts right, shifting in 1s,
 *    so reads after the 8 buttons return 1 (open-bus-style behavior).
 *
 * Headless-safe: no DOM or browser APIs. Input sources call setButton().
 */
export class StandardController implements Controller {
    /** Current physical button state, indexed by Button. */
    private buttons: boolean[] = new Array(8).fill(false);
    /** Latched shift register. */
    private shiftRegister: number = 0;
    /** Current strobe line level (bit 0 of the last $4016 write). */
    private strobe: number = 0;

    setButton(button: Button, pressed: boolean): void {
        this.buttons[button & 0x07] = pressed;
    }

    /** Set all buttons at once from a bitmask (bit 0 = A ... bit 7 = Right). */
    setButtonMask(mask: number): void {
        for (let i = 0; i < 8; i++) {
            this.buttons[i] = (mask & (1 << i)) !== 0;
        }
    }

    /** Current physical button state as a bitmask (bit 0 = A ... bit 7 = Right). */
    getButtonMask(): number {
        let mask = 0;
        for (let i = 0; i < 8; i++) {
            if (this.buttons[i]) mask |= 1 << i;
        }
        return mask;
    }

    private latch(): void {
        this.shiftRegister = this.getButtonMask();
    }

    writeStrobe(value: number): void {
        const bit = value & 0x01;
        if (this.strobe === 1 && bit === 0) {
            // Falling edge: latch current button state once.
            this.latch();
        }
        this.strobe = bit;
        if (this.strobe === 1) {
            // Strobe held high: continuously reload from live button state.
            this.latch();
        }
    }

    read(): number {
        if (this.strobe === 1) {
            // Continuous reload: always report button A's live state.
            this.latch();
        }
        const bit = this.shiftRegister & 0x01;
        this.shiftRegister = (this.shiftRegister >> 1) | 0x80; // shift in 1s
        return bit;
    }
}
